import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Shield, Lock, Eye, Database } from 'lucide-react';
import './LegalPages.css';

const Privacy = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <div className="legal-page">
      {/* Header */}
      <header className="legal-header">
        <div className="legal-header-content">
          <button onClick={() => navigate('/')} className="back-button">
            <ArrowLeft size={20} />
            <span>{t('privacy.header.backButton')}</span>
          </button>
          <div className="legal-logo" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
            <img src="/assets/logo/alpenscreen-horizontal-transparent.svg" alt="AlpenScreen" className="logo-img" />
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="legal-container">
        <div className="legal-content">
          <div className="legal-header-section">
            <Shield size={48} color="#2B7FD7" />
            <h1 className="legal-title">{t('privacy.title')}</h1>
            <p className="legal-date">{t('privacy.lastUpdated')}</p>
          </div>

          <div className="privacy-highlights">
            <div className="highlight-card">
              <Lock size={24} color="#2B7FD7" />
              <h3>{t('privacy.highlights.gdpr.title')}</h3>
              <p>{t('privacy.highlights.gdpr.description')}</p>
            </div>
            <div className="highlight-card">
              <Database size={24} color="#2B7FD7" />
              <h3>{t('privacy.highlights.hosting.title')}</h3>
              <p>{t('privacy.highlights.hosting.description')}</p>
            </div>
            <div className="highlight-card">
              <Eye size={24} color="#2B7FD7" />
              <h3>{t('privacy.highlights.transparent.title')}</h3>
              <p>{t('privacy.highlights.transparent.description')}</p>
            </div>
          </div>

          <section className="legal-section">
            <h2>{t('privacy.section1.title')}</h2>
            <p>{t('privacy.section1.intro')}</p>
            <div className="info-box">
              <strong>AlpenScreen</strong><br />
              Stefano Rizzo<br />
              Im Stadtwald 5<br />
              9400 Rorschach<br />
              {t('privacy.section1.country')}<br />
              <br />
              {t('privacy.section1.email')}: privacy@alpenscreen.ch<br />
              {t('privacy.section1.phone')}: +41 76 718 41 00
            </div>
          </section>

          <section className="legal-section">
            <h2>{t('privacy.section2.title')}</h2>
            
            <h3>{t('privacy.section2.sub1.title')}</h3>
            <p>{t('privacy.section2.sub1.intro')}</p>
            <p>{t('privacy.section2.sub1.collected')}</p>
            <ul>
              <li>{t('privacy.section2.sub1.data.ip')}</li>
              <li>{t('privacy.section2.sub1.data.datetime')}</li>
              <li>{t('privacy.section2.sub1.data.filename')}</li>
              <li>{t('privacy.section2.sub1.data.referrer')}</li>
              <li>{t('privacy.section2.sub1.data.browser')}</li>
            </ul>
            <p>{t('privacy.section2.sub1.purposes')}</p>
            <ul>
              <li>{t('privacy.section2.sub1.purpose.connection')}</li>
              <li>{t('privacy.section2.sub1.purpose.comfort')}</li>
              <li>{t('privacy.section2.sub1.purpose.security')}</li>
              <li>{t('privacy.section2.sub1.purpose.admin')}</li>
            </ul>
            <p>{t('privacy.section2.sub1.legal')}</p>

            <h3>{t('privacy.section2.sub2.title')}</h3>
            <p>{t('privacy.section2.sub2.intro')}</p>
            <ul>
              <li>{t('privacy.section2.sub2.data.email')}</li>
              <li>{t('privacy.section2.sub2.data.password')}</li>
              <li>{t('privacy.section2.sub2.data.name')}</li>
              <li>{t('privacy.section2.sub2.data.company')}</li>
              <li>{t('privacy.section2.sub2.data.phone')}</li>
            </ul>
            <p>{t('privacy.section2.sub2.purpose')}</p>

            <h3>{t('privacy.section2.sub3.title')}</h3>
            <p>{t('privacy.section2.sub3.description')}</p>
          </section>

          <section className="legal-section">
            <h2>{t('privacy.section3.title')}</h2>
            <p>{t('privacy.section3.intro')}</p>
            <p>{t('privacy.section3.conditions')}</p>
            <ul>
              <li>{t('privacy.section3.condition.consent')}</li>
              <li>{t('privacy.section3.condition.legal')}</li>
              <li>{t('privacy.section3.condition.obligation')}</li>
            </ul>
            <p>{t('privacy.section3.providers')}</p>
            <ul>
              <li><strong>Hetzner Online GmbH:</strong> {t('privacy.section3.provider.hetzner')}</li>
              <li><strong>Stripe Inc.:</strong> {t('privacy.section3.provider.stripe')}</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>{t('privacy.section4.title')}</h2>
            <p>{t('privacy.section4.intro')}</p>
            <p>{t('privacy.section4.types')}</p>
            <ul>
              <li><strong>{t('privacy.section4.type.necessary.title')}:</strong> {t('privacy.section4.type.necessary.description')}</li>
              <li><strong>{t('privacy.section4.type.functional.title')}:</strong> {t('privacy.section4.type.functional.description')}</li>
            </ul>
            <p>{t('privacy.section4.legal')}</p>
          </section>

          <section className="legal-section">
            <h2>{t('privacy.section5.title')}</h2>
            <p>{t('privacy.section5.intro')}</p>
            
            <h3>{t('privacy.section5.right.access.title')}</h3>
            <p>{t('privacy.section5.right.access.description')}</p>

            <h3>{t('privacy.section5.right.rectification.title')}</h3>
            <p>{t('privacy.section5.right.rectification.description')}</p>

            <h3>{t('privacy.section5.right.erasure.title')}</h3>
            <p>{t('privacy.section5.right.erasure.description')}</p>

            <h3>{t('privacy.section5.right.restriction.title')}</h3>
            <p>{t('privacy.section5.right.restriction.description')}</p>

            <h3>{t('privacy.section5.right.portability.title')}</h3>
            <p>{t('privacy.section5.right.portability.description')}</p>

            <h3>{t('privacy.section5.right.objection.title')}</h3>
            <p>{t('privacy.section5.right.objection.description')}</p>

            <h3>{t('privacy.section5.right.complaint.title')}</h3>
            <p>{t('privacy.section5.right.complaint.description')}</p>
          </section>

          <section className="legal-section">
            <h2>{t('privacy.section6.title')}</h2>
            <p>{t('privacy.section6.ssl')}</p>
            <p>{t('privacy.section6.security')}</p>
          </section>

          <section className="legal-section">
            <h2>{t('privacy.section7.title')}</h2>
            <p>{t('privacy.section7.intro')}</p>
            <p>{t('privacy.section7.periods')}</p>
            <ul>
              <li><strong>{t('privacy.section7.period.account.title')}:</strong> {t('privacy.section7.period.account.duration')}</li>
              <li><strong>{t('privacy.section7.period.media.title')}:</strong> {t('privacy.section7.period.media.duration')}</li>
              <li><strong>{t('privacy.section7.period.logs.title')}:</strong> {t('privacy.section7.period.logs.duration')}</li>
              <li><strong>{t('privacy.section7.period.invoices.title')}:</strong> {t('privacy.section7.period.invoices.duration')}</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>{t('privacy.section8.title')}</h2>
            <p>{t('privacy.section8.intro')}</p>
            <div className="info-box">
              <strong>{t('privacy.section8.contact')}:</strong><br />
              {t('privacy.section8.email')}: privacy@alpenscreen.ch<br />
              {t('privacy.section8.phone')}: +41 76 718 41 00<br />
              <br />
              {t('privacy.section8.postal')}
            </div>
          </section>

          <section className="legal-section">
            <h2>{t('privacy.section9.title')}</h2>
            <p>{t('privacy.section9.intro')}</p>
            <p>{t('privacy.section9.newVisit')}</p>
          </section>
        </div>
      </div>

      {/* Footer */}
      <footer className="legal-footer">
        <div className="legal-footer-content">
          <p>{t('privacy.footer.copyright')}</p>
          <div className="legal-footer-links">
            <button onClick={() => navigate('/support')} className="footer-link">{t('privacy.footer.support')}</button>
            <span>•</span>
            <button onClick={() => navigate('/terms')} className="footer-link">{t('privacy.footer.terms')}</button>
            <span>•</span>
            <button onClick={() => navigate('/imprint')} className="footer-link">{t('privacy.footer.imprint')}</button>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Privacy;